/** Os quatro números do topo (SPEC-OPP-022, DESIGN.md § 3.2).
 *
 * Eles ocupam o espaço que antes ficava vazio acima das colunas, e respondem
 * sem rolagem o que o board só responde somando coluna a coluna: quanto há em
 * jogo, quanto entrou no mês, de que tamanho é um negócio típico aqui, e
 * quanto do que fecha é ganho.
 *
 * **Ausência não é zero.** Ticket médio e conversão chegam nulos quando não
 * houve negócio no mês, e a barra mostra um traço com a explicação embaixo.
 * Escrever "0%" acusaria um desempenho ruim que não aconteceu.
 */

import { formatarDinheiro } from "./formato";
import { useMetricas, type Aguardando } from "./useQuadro";

import type { Indicadores } from "./api";

export function BarraDeMetricas({
  esperas = [],
  emTempoReal = false,
}: {
  /** As mesmas do board: enquanto a projeção não alcança, os números releem
   * rápido em vez de discordar das colunas por até vinte segundos. */
  esperas?: readonly Aguardando[];
  emTempoReal?: boolean;
} = {}) {
  const consulta = useMetricas(esperas, emTempoReal);

  // Sem bloco de erro próprio: o board logo abaixo já avisa quando a API não
  // responde, e dois alertas para a mesma queda são ruído.
  if (consulta.isError) return null;

  const indicadores = consulta.data;

  return (
    <dl
      data-guia="barra-de-metricas"
      className="grid grid-cols-2 overflow-hidden rounded-lg border border-borda bg-superficie md:grid-cols-4"
    >
      {(indicadores === undefined ? esqueleto() : ler(indicadores)).map((tile) => (
        <div
          key={tile.rotulo}
          className="flex flex-col gap-1.5 border-borda px-5 py-4 [&:not(:last-child)]:border-r"
        >
          <dt className="font-texto text-micro uppercase tracking-wide text-texto-suave">
            {tile.rotulo}
          </dt>
          {/* `dd` antes da dica, e a dica dentro dele: uma `<p>` solta entre
              `dt` e `dd` quebraria a lista de descrição. */}
          <dd className="flex flex-col gap-1">
            <span
              className={[
                "font-dados text-title tabular-nums",
                tile.valor === TRACO ? "text-texto-tenue" : "text-texto",
              ].join(" ")}
            >
              {tile.valor}
            </span>
            <span className="font-texto text-caption text-texto-suave">{tile.dica}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Travessão, e não hífen: é o sinal tipográfico de "não se aplica". */
const TRACO = "—";

interface Tile {
  rotulo: string;
  valor: string;
  dica: string;
}

function esqueleto(): Tile[] {
  // Enquanto carrega, os rótulos já ficam no lugar. Uma barra que aparece do
  // nada empurraria o board para baixo depois que a pessoa já começou a ler.
  return [
    { rotulo: "Pipeline aberto", valor: TRACO, dica: "carregando…" },
    { rotulo: "Ganho no mês", valor: TRACO, dica: "carregando…" },
    { rotulo: "Ticket médio", valor: TRACO, dica: "carregando…" },
    { rotulo: "Taxa de conversão", valor: TRACO, dica: "carregando…" },
  ];
}

/** Há negócio aberto, e nenhum com valor informado?
 *
 * A distinção que falta ao read model: ele soma centavos e conta linhas, e não
 * diz quantas das linhas tinham valor. Soma zero com contagem positiva só
 * ocorre quando ninguém preencheu nada — negócio fechado a zero é raro e não
 * fica "aberto".
 */
function semValorInformado(m: Indicadores): boolean {
  return m.open_count > 0 && m.open_value_cents === 0;
}

function dicaDoPipeline(m: Indicadores): string {
  const quantas = `${String(m.open_count)} ${m.open_count === 1 ? "oportunidade ativa" : "oportunidades ativas"}`;
  // Funil vazio cai aqui sem tratamento próprio: `semValorInformado` já exige
  // negócio aberto, e zero somando zero é verdade — não há o que acusar. Um
  // `if (open_count === 0)` a mais parecia defensivo e não mudava nada; a
  // mutação mostrou que apagá-lo deixava a suíte verde.
  return semValorInformado(m) ? `${quantas}, nenhuma com valor informado` : quantas;
}

function ler(m: Indicadores): Tile[] {
  const fechadas = m.won_month_count + m.lost_month_count;

  return [
    {
      rotulo: "Pipeline aberto",
      // "R$ 0" com quatro negócios andando ensina a desconfiar da barra — e
      // métrica em que não se confia deixa de ser lida. O DESIGN.md § 3.2 já
      // dizia: "'R$ 0' se leria como 'os negócios valem nada'".
      //
      // O travessão é o mesmo recurso que o ticket médio e a conversão já usam
      // logo ao lado para dizer "não há o que somar".
      valor: semValorInformado(m) ? TRACO : formatarDinheiro(m.open_value_cents, "BRL"),
      dica: dicaDoPipeline(m),
    },
    {
      rotulo: "Ganho no mês",
      valor: formatarDinheiro(m.won_month_value_cents, "BRL"),
      dica: `${String(m.won_month_count)} ${m.won_month_count === 1 ? "negócio fechado" : "negócios fechados"}`,
    },
    {
      rotulo: "Ticket médio",
      valor:
        m.average_ticket_cents === null ? TRACO : formatarDinheiro(m.average_ticket_cents, "BRL"),
      dica: dicaDoTicket(m),
    },
    {
      rotulo: "Taxa de conversão",
      valor: m.conversion_rate === null ? TRACO : `${String(m.conversion_rate)}%`,
      dica:
        fechadas === 0
          ? "nada fechou neste mês"
          : `${String(m.won_month_count)} de ${String(fechadas)} fechadas`,
    },
  ];
}

/** A variação só aparece quando os dois meses têm base.
 *
 * Comparar contra um mês sem negócio nenhum produziria "+∞%", e comparar
 * contra nada produziria um número inventado.
 */
function dicaDoTicket(m: Indicadores): string {
  if (m.average_ticket_cents === null) return "sem negócios ganhos neste mês";
  if (m.previous_average_ticket_cents === null || m.previous_average_ticket_cents === 0) {
    return "primeiro mês com negócio ganho";
  }

  const variacao = Math.round(
    ((m.average_ticket_cents - m.previous_average_ticket_cents) / m.previous_average_ticket_cents) *
      100,
  );
  if (variacao === 0) return "estável em relação ao mês anterior";
  return `${variacao > 0 ? "+" : ""}${String(variacao)}% em relação ao mês anterior`;
}
